import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Chip,
  type CSSVariablesResolver,
  createTheme,
  Drawer,
  defaultVariantColorsResolver,
  Input,
  isLightColor,
  type MantineThemeOverride,
  Menu,
  Modal,
  Notification,
  Pagination,
  Paper,
  Popover,
  rem,
  SegmentedControl,
  Switch,
  Tabs,
  Tooltip,
  type VariantColorsResolver,
} from '@mantine/core'
import { Day } from '@mantine/dates'
import type { Tone } from '@tnn/core'
import {
  avatarColors,
  hueColors,
  neutralShades,
  ordinalRamp,
  primaryShades,
  type Roles,
  roles,
  semanticShades,
  toneColors,
} from './palette'

const ON_DARK = '#ffffff'
const ON_LIGHT = '#1b1b1f'

/**
 * Text on filled buttons, badges and the like, per colour scheme. Mantine
 * picks white or black from the light-scheme shade only, which put white
 * text on the pale dark-scheme fills. The app colour uses Material's
 * on-primary; other colours get whichever of white/near-black reads on that
 * scheme's shade.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const colors = defaultVariantColorsResolver(input)
  if (input.variant !== 'filled') return colors
  const key = input.color ?? input.theme.primaryColor
  if (key === input.theme.primaryColor) return { ...colors, color: 'var(--tnn-on-primary)' }
  const palette = input.theme.colors[key]
  if (!palette) return colors
  const shade = input.theme.primaryShade
  const [light, dark] = typeof shade === 'number' ? [shade, shade] : [shade.light, shade.dark]
  const on = (fill: string) => (isLightColor(fill, 0.4) ? ON_LIGHT : ON_DARK)
  return { ...colors, color: `light-dark(${on(palette[light])}, ${on(palette[dark])})` }
}

export const FONT_FAMILY = '"Google Sans Variable", "Google Sans", "Noto Sans Thai", system-ui, sans-serif'

export interface AppThemeOptions {
  /** App seed colour (see `apps` in `@tnn/core`) */
  seed: string
  /** User text-size preference: 0.9 small, 1 normal, 1.1 large */
  scale?: number
  /** Screen-reader name of the close button on dialogs, sheets and toasts */
  closeLabel?: string
}

/**
 * Mantine theme for one app. Everything visual that should be the same in
 * every app lives here; apps only pass their seed colour.
 */
export function createAppTheme({ seed, scale = 1, closeLabel = 'Close' }: AppThemeOptions): MantineThemeOverride {
  const closeButtonProps = { 'aria-label': closeLabel }
  return createTheme({
    scale,
    fontFamily: FONT_FAMILY,
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    headings: {
      fontFamily: FONT_FAMILY,
      fontWeight: '500',
      sizes: {
        h1: { fontSize: rem(28), lineHeight: '1.3' },
        h2: { fontSize: rem(22), lineHeight: '1.35' },
        h3: { fontSize: rem(18), lineHeight: '1.4' },
        h4: { fontSize: rem(16), lineHeight: '1.45' },
        h5: { fontSize: rem(14), lineHeight: '1.5' },
        h6: { fontSize: rem(13), lineHeight: '1.5' },
      },
    },
    // Thai stacks vowels and tone marks above and below the line, so line
    // heights stay generous even in dense tables.
    fontSizes: { xs: rem(12), sm: rem(13), md: rem(14), lg: rem(16), xl: rem(20) },
    lineHeights: { xs: '1.45', sm: '1.5', md: '1.55', lg: '1.55', xl: '1.45' },
    fontWeights: { regular: '400', medium: '500', bold: '700' },
    radius: { xs: rem(4), sm: rem(8), md: rem(12), lg: rem(16), xl: rem(28) },
    defaultRadius: 'sm',
    spacing: { xs: rem(4), sm: rem(8), md: rem(16), lg: rem(24), xl: rem(32) },
    colors: { primary: primaryShades(seed), gray: neutralShades(seed), ...semanticShades() },
    primaryColor: 'primary',
    primaryShade: { light: 6, dark: 2 },
    variantColorResolver,
    cursorType: 'pointer',
    respectReducedMotion: true,
    shadows: {
      xs: '0 1px 2px rgb(0 0 0 / 0.12)',
      sm: '0 1px 2px rgb(0 0 0 / 0.14), 0 1px 3px 1px rgb(0 0 0 / 0.08)',
      md: '0 1px 2px rgb(0 0 0 / 0.14), 0 2px 6px 2px rgb(0 0 0 / 0.08)',
      lg: '0 2px 3px rgb(0 0 0 / 0.14), 0 6px 10px 4px rgb(0 0 0 / 0.08)',
      xl: '0 4px 4px rgb(0 0 0 / 0.14), 0 8px 12px 6px rgb(0 0 0 / 0.08)',
    },
    breakpoints: { xs: '36em', sm: '48em', md: '62em', lg: '75em', xl: '90em' },
    components: {
      Button: Button.extend({ defaultProps: { radius: 'xl' } }),
      ActionIcon: ActionIcon.extend({ defaultProps: { radius: 'xl', variant: 'subtle', color: 'gray' } }),
      Input: Input.extend({ defaultProps: { radius: 'sm' } }),
      Paper: Paper.extend({ defaultProps: { radius: 'lg' } }),
      Card: Card.extend({ defaultProps: { radius: 'lg' } }),
      Badge: Badge.extend({ defaultProps: { radius: 'xl', variant: 'light' } }),
      Chip: Chip.extend({ defaultProps: { radius: 'sm', variant: 'light' } }),
      Checkbox: Checkbox.extend({ defaultProps: { radius: 'xs' } }),
      Switch: Switch.extend({ defaultProps: { size: 'md' } }),
      SegmentedControl: SegmentedControl.extend({ defaultProps: { radius: 'xl' } }),
      Tabs: Tabs.extend({ defaultProps: { radius: 'sm' } }),
      Pagination: Pagination.extend({ defaultProps: { radius: 'xl' } }),
      // Dialog headlines read like Material's: large and regular weight, in confirm dialogs too
      Modal: Modal.extend({
        defaultProps: { radius: 'xl', centered: true, overlayProps: { backgroundOpacity: 0.32, blur: 0 }, closeButtonProps },
        styles: { title: { fontSize: rem(20), fontWeight: 400, lineHeight: 1.4 } },
      }),
      Drawer: Drawer.extend({
        defaultProps: { overlayProps: { backgroundOpacity: 0.32, blur: 0 }, closeButtonProps },
        // A sheet from the bottom (a phone's action sheets) is rounded only on top, where it meets the page
        styles: (_theme, props) => ({
          title: { fontSize: rem(20), fontWeight: 400, lineHeight: 1.4 },
          content: props.position === 'bottom' ? { borderEndStartRadius: 0, borderEndEndRadius: 0 } : {},
        }),
      }),
      Menu: Menu.extend({ defaultProps: { radius: 'sm', shadow: 'md', position: 'bottom-end' } }),
      Popover: Popover.extend({ defaultProps: { radius: 'md', shadow: 'md' } }),
      Tooltip: Tooltip.extend({ defaultProps: { radius: 'sm', openDelay: 400, withinPortal: true } }),
      Notification: Notification.extend({ defaultProps: { radius: 'md', closeButtonProps } }),
      // Every calendar marks today (styled in global.css)
      Day: Day.extend({ defaultProps: { highlightToday: true } }),
    },
    other: { seed },
  })
}

const toneNames: Tone[] = ['neutral', 'info', 'progress', 'success', 'warning', 'danger']

function vars(
  r: Roles,
  tones: ReturnType<typeof toneColors>,
  inverseTones: ReturnType<typeof toneColors>,
  ramp: string[],
  dark: boolean,
): Record<string, string> {
  const out: Record<string, string> = {
    // Mantine's own variables, mapped onto Material roles
    '--mantine-color-body': r.surface,
    '--mantine-color-text': r.onSurface,
    '--mantine-color-dimmed': r.onSurfaceVariant,
    '--mantine-color-placeholder': r.onSurfaceVariant,
    '--mantine-color-anchor': r.primary,
    '--mantine-color-error': r.error,
    '--mantine-color-default': r.surfaceContainerLowest,
    '--mantine-color-default-hover': r.surfaceContainerLow,
    '--mantine-color-default-color': r.onSurface,
    '--mantine-color-default-border': r.outlineVariant,
    // Platform tokens used by the shell and components
    '--tnn-canvas': r.surface,
    '--tnn-sheet': dark ? r.surfaceContainerLow : r.surfaceContainerLowest,
    '--tnn-surface-low': r.surfaceContainerLow,
    '--tnn-surface': r.surfaceContainer,
    '--tnn-surface-high': r.surfaceContainerHigh,
    '--tnn-surface-highest': r.surfaceContainerHighest,
    '--tnn-on-surface': r.onSurface,
    '--tnn-on-surface-variant': r.onSurfaceVariant,
    '--tnn-outline': r.outline,
    '--tnn-outline-variant': r.outlineVariant,
    '--tnn-primary': r.primary,
    '--tnn-on-primary': r.onPrimary,
    '--tnn-primary-container': r.primaryContainer,
    '--tnn-on-primary-container': r.onPrimaryContainer,
    '--tnn-secondary-container': r.secondaryContainer,
    '--tnn-on-secondary-container': r.onSecondaryContainer,
    '--tnn-error': r.error,
    '--tnn-error-container': r.errorContainer,
    '--tnn-on-error-container': r.onErrorContainer,
    '--tnn-inverse-surface': r.inverseSurface,
    '--tnn-inverse-on-surface': r.inverseOnSurface,
    '--tnn-inverse-primary': r.inversePrimary,
    '--tnn-scrim': r.scrim,
    '--tnn-selection': r.primaryContainer,
    // Inputs are filled, borderless fields: editable ones on the strongest container tone,
    // read-only values on a quieter tint of the sheet, errors tinted red; the one being typed in
    // takes a tint of the app colour (no ring)
    '--tnn-field': r.surfaceContainerHighest,
    '--tnn-field-hover': `color-mix(in srgb, ${r.onSurface} 6%, ${r.surfaceContainerHighest})`,
    '--tnn-field-focus': `color-mix(in srgb, ${r.primary} 14%, ${r.surfaceContainerHighest})`,
    '--tnn-field-readonly': `color-mix(in srgb, ${r.onSurface} 5%, ${dark ? r.surfaceContainerLow : r.surfaceContainerLowest})`,
    '--tnn-field-error': `color-mix(in srgb, ${r.error} 14%, ${r.surfaceContainerHighest})`,
    // State layers (Material: 8% hover, 12% press) in the on-surface colour
    // Material state layers: 8% hover, 12% press, of the content colour
    '--tnn-hover': dark ? 'rgb(255 255 255 / 0.08)' : 'rgb(0 0 0 / 0.08)',
    '--tnn-press': dark ? 'rgb(255 255 255 / 0.12)' : 'rgb(0 0 0 / 0.12)',
  }
  ramp.forEach((hex, i) => {
    out[`--tnn-ramp-${i + 1}`] = hex
  })
  for (const tone of toneNames) {
    out[`--tnn-tone-${tone}-bg`] = tones[tone].bg
    out[`--tnn-tone-${tone}-fg`] = tones[tone].fg
    out[`--tnn-tone-${tone}-dot`] = tones[tone].dot
    // On the inverse surface (toasts) a status icon takes the other scheme's dot
    out[`--tnn-tone-${tone}-inverse`] = inverseTones[tone].dot
  }
  for (const [hue, colors] of Object.entries(hueColors(dark))) {
    out[`--tnn-hue-${hue}-bg`] = colors.bg
    out[`--tnn-hue-${hue}-fg`] = colors.fg
    out[`--tnn-hue-${hue}-dot`] = colors.dot
  }
  return out
}

/** Emits the Material role variables for light and dark mode. */
export function createCssVariablesResolver(seed: string): CSSVariablesResolver {
  const lightTones = toneColors(seed, false)
  const darkTones = toneColors(seed, true)
  const light = vars(roles(seed, false), lightTones, darkTones, ordinalRamp(seed, false), false)
  const dark = vars(roles(seed, true), darkTones, lightTones, ordinalRamp(seed, true), true)
  const avatars = { ...Object.fromEntries(avatarColors().map((hex, i) => [`--tnn-avatar-${i + 1}`, hex])), '--tnn-on-avatar': '#ffffff' }
  return () => ({ variables: avatars, light, dark })
}
